class AnnotationApiService {
  constructor() {
    this.baseUrl = null
  }

  setBaseUrl(appname) {
    this.baseUrl = `/base/${appname}`
  }

  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }

  ensureBaseUrl() {
    if (!this.baseUrl) {
      throw new Error('AnnotationApiService: baseUrl not set. Call setBaseUrl() first.')
    }
  }

  async fetchAnnotations(documentId) {
    this.ensureBaseUrl()

    const url = `${this.baseUrl}/documents/${documentId}/annotations`

    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': this.getCsrfToken()
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      if (response.status === 401) {
        return []
      }
      throw new Error(`Failed to fetch annotations: ${response.statusText}`)
    }

    const data = await response.json()
    return this.transformFromApi(data)
  }

  async createAnnotation(annotation) {
    this.ensureBaseUrl()
    const response = await fetch(
      `${this.baseUrl}/documents/${annotation.documentId}/annotations`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          annotation: this.transformToApi(annotation)
        })
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('UNAUTHORIZED')
      }
      throw new Error(`Failed to create annotation: ${response.statusText}`)
    }

    const data = await response.json()
    return this.transformFromApi([data])[0]
  }

  async deleteAnnotation(annotationId, documentId) {
    this.ensureBaseUrl()
    const response = await fetch(
      `${this.baseUrl}/documents/${documentId}/annotations/${annotationId}`,
      {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        credentials: 'same-origin'
      }
    )

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('FORBIDDEN')
      }
      throw new Error(`Failed to delete annotation: ${response.statusText}`)
    }

    return true
  }

  async updateAnnotation(annotationId, documentId, updates) {
    this.ensureBaseUrl()
    const response = await fetch(
      `${this.baseUrl}/documents/${documentId}/annotations/${annotationId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          annotation: this.transformUpdatesToApi(updates)
        })
      }
    )

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('FORBIDDEN')
      }
      throw new Error(`Failed to update annotation: ${response.statusText}`)
    }

    const data = await response.json()
    return this.transformFromApi([data])[0]
  }

  transformFromApi(data) {
    let items = data
    if (data.objects) {
      items = data.objects
    } else if (data.object) {
      items = [data.object]
    } else if (!Array.isArray(data)) {
      items = [data]
    }

    if (!Array.isArray(items)) {
      items = [items]
    }

    return items.filter(item => item != null).map(item => {
      const sectionId = item.section_id
      const isTableAnnotation = /^.+-\d+-t\d+-r\d+-c\d+$/.test(sectionId)

      let tableId = null
      let cellId = null
      let paragraphIndex = item.paragraph_index

      if (isTableAnnotation) {
        cellId = sectionId
        const tableMatch = sectionId.match(/^(.+-t\d+)-r\d+-c\d+$/)
        if (tableMatch) {
          tableId = tableMatch[1]
        }
        paragraphIndex = 0
      }

      return {
        id: (item.id && item.id.toString()) || item.annotation_id,
        documentId: item.document_id && item.document_id.toString(),
        sectionId: sectionId,
        paragraphIndex: paragraphIndex,
        startOffset: item.start_offset,
        endOffset: item.end_offset,
        selectedText: item.selected_text,
        type: item.annotation_type,
        color: item.color,
        noteContent: item.note_content,
        isTableAnnotation: isTableAnnotation,
        cellId: cellId,
        tableId: tableId
      }
    })
  }

  transformToApi(annotation) {
    const gon = window.gon || {}

    const sectionId = annotation.isTableAnnotation && annotation.cellId
      ? annotation.cellId
      : annotation.sectionId
    const paragraphIndex = annotation.isTableAnnotation ? 0 : annotation.paragraphIndex

    return {
      document_id: annotation.documentId,
      section_id: sectionId,
      paragraph_index: paragraphIndex,
      start_offset: annotation.startOffset,
      end_offset: annotation.endOffset,
      selected_text: annotation.selectedText,
      annotation_type: annotation.type,
      color: annotation.color,
      note_content: annotation.noteContent,
      tolgeo: gon.app_tolgeo || 'latam',
      paisiso: gon.paisiso || gon.current_app,
      user_subscription_id: gon.subscription_id
    }
  }

  transformUpdatesToApi(updates) {
    const result = {}
    const mapping = {
      noteContent: 'note_content',
      startOffset: 'start_offset',
      endOffset: 'end_offset',
      color: 'color'
    }

    for (const [key, value] of Object.entries(updates)) {
      if (mapping[key]) {
        result[mapping[key]] = value
      }
    }

    return result
  }
}

const annotationApiService = new AnnotationApiService()

export default annotationApiService
